import React, {
  useState, useEffect, useRef,
} from 'react';
import { getEnv } from '@/common/agents';
import { message } from '$/components/Message';
import { useOfficialStore } from '$/store/useOfficialStore';
import { type OverseaFeatureItem } from '@/apiSdk/api.type';
import { useCommonStore } from '@/store/useCommonStore';
import preQrCode from '@/assets/pre-qrcode.png';
import betaQrCode from '@/assets/beta-qrcode.png';
import relaseQrCode from '@/assets/release-qrcode.png';
import ServerHeader, { type OverseaOfficialHeaderAppearance } from './ServerHeader';
import styles from './index.module.scss';

/**
 * 客户端 Header 负责登录态、下载弹窗与滚动遮罩，appearance 可控制首屏是否默认带遮罩。
 */
export default function ClientHeader({
  showAlert,
  menus,
  appearance,
}: {
  showAlert?: boolean;
  menus: Record<'product' | 'blog', OverseaFeatureItem[]>;
  appearance?: OverseaOfficialHeaderAppearance;
}) {
  const [scrolled, setScrolled] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const [mounted, setMounted] = useState(false);
  const isOnline = useCommonStore((state) => state.isOnline);
  const toLogin = useCommonStore((state) => state.toLogin);
  // const initUserInfo = useCommonStore((state) => state.initUserInfo);
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  // const isDev = getEnv().ENV === 'development';

  // 确保首次渲染与服务端一致，mount 后再使用真实登录态
  useEffect(() => {
    window.scrollTo(0, 0);
    setMounted(true);
    console.log('[菜单数据]', menus);
  }, []);

  // 获取首页路径
  // const getHomePath = useCallback(() => {
  //   return isDev ? '/--roboneo/home' : '/home';
  // }, [isDev]);

  // 检查登录态，如果已登录则跳转到首页
  // useEffect(() => {
  //   console.log('isUserLoggedIn', isUserLoggedIn);
  //   if (isDev) return;
  //   if (isUserLoggedIn) {
  //     window.location.href = getHomePath();
  //   }
  // }, [isUserLoggedIn, getHomePath, isDev]);

  // 登录处理函数
  const handleLogin = async () => {
    window.mtstat?.track('roboneo_official_page_icon_click', {
      icon_id: 'login',
    });
    if (isOnline) {
      try {
        await toLogin();
        // await initUserInfo();
        // 登录成功后跳转到首页
        // window.location.href = homePath;
        window.location.reload();
      } catch (error) {
        console.error('登录失败:', error);
        message.error('RoboNeo_Web_network_fail_retry');
      }
    } else {
      message.error('RoboNeo_Web_network_fail_retry');
    }
  };

  //   22 是alert高度的一半。看最终视觉效果，可以微调，无逻辑影响
  const SCROLL_ALL_DISTANCE = showAlert ? 22 : 100;

  useEffect(() => {
    // 在页面顶部100px处创建一个占位元素，当它离开视口时表示已滚动超过100px
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        console.log('entries', entries[0].isIntersecting, window.scrollY || window.pageYOffset);
        // 当占位元素不可见时，说明已滚动超过100px
        setScrolled(!entries[0].isIntersecting);
      },
      {
        // rootMargin: '-100px 0px' 表示在视口顶部100px处设置一个观察点
        rootMargin: `${SCROLL_ALL_DISTANCE - 1}px 0px 0px 0px`,
        threshold: 0,
      },
    );

    observer.observe(sentinel);

    // 初始检查一次，处理页面加载时已经滚动的情况
    const scrollY = window.scrollY || window.pageYOffset;
    setScrolled(scrollY >= 100);

    const cleanup = () => {
      observer.disconnect();
    };
    return cleanup;
  }, [SCROLL_ALL_DISTANCE]);

  const downloadContent = () => {
    const QR_CODE = {
      development: preQrCode,
      dev: preQrCode,
      pre: preQrCode,
      beta: betaQrCode,
      release: relaseQrCode,
    }[getEnv().ENV];

    return (
      <div className={styles.downloadPopverContent}>
        <div className={styles.qrcodeBox}>
          <img className={styles.qrcode} src={QR_CODE.src} alt="qrcode" />
        </div>
      </div>
    );
  };

  return (
    <>
      {/* 占位元素，用于 IntersectionObserver 监听 */}
      <div
        ref={sentinelRef}
        className={styles.sentinel}
      />
      <ServerHeader
        showAlert={showAlert}
        scrolled={scrolled}
        appearance={appearance}
        downloadContent={downloadContent()}
        menus={menus}
        onLoginClick={handleLogin}
        isLoggedIn={mounted && isUserLoggedIn}
      />
    </>
  );
}
